import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Check, X, MessageSquare } from "lucide-react";
import api from "@/lib/api";

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const url = filterStatus ? `/admin/reviews?status=${filterStatus}` : "/admin/reviews";
    api.get(url).then((r) => setReviews(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filterStatus]);

  const moderate = async (id, status, reply = "") => {
    await api.put(`/admin/reviews/${id}`, { status, admin_reply: reply });
    setReplyId(null); setReplyText("");
    load();
  };

  const STATUSES = [
    { value: "", label: "Все" },
    { value: "pending", label: "На модерации" },
    { value: "published", label: "Опубликованы" },
    { value: "rejected", label: "Отклонены" },
  ];

  return (
    <div data-testid="admin-reviews">
      <h1 className="font-serif text-3xl mb-6">Отзывы</h1>

      <div className="flex gap-2 mb-6">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilterStatus(s.value)}
            className={`px-3 py-1 text-xs ${filterStatus === s.value ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-white/10"}`}
            data-testid={`filter-review-${s.value || "all"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-card animate-pulse" />)}</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="no-reviews">Нет отзывов</div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} className="bg-card border border-white/5 p-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">{r.client_name}</span>
                    <span className="text-xs text-muted-foreground">{r.client_phone}</span>
                    <span className={`text-xs px-2 py-0.5 ${r.status === "pending" ? "bg-yellow-900/30 text-yellow-400" : r.status === "published" ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}`}>
                      {r.status === "pending" ? "Модерация" : r.status === "published" ? "Опубликован" : "Отклонен"}
                    </span>
                  </div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "text-primary fill-primary" : "text-muted-foreground/30"}`} />
                    ))}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{r.text}</p>
              {r.admin_reply && (
                <div className="pl-4 border-l-2 border-primary/30 mb-3">
                  <p className="text-xs text-primary mb-1">Ваш ответ:</p>
                  <p className="text-sm text-muted-foreground">{r.admin_reply}</p>
                </div>
              )}

              {/* Reply form */}
              {replyId === r.id && (
                <div className="mb-3 space-y-2">
                  <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Ваш ответ..." className="bg-background border-white/10 rounded-none" data-testid={`reply-textarea-${r.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => moderate(r.id, "published", replyText)} className="bg-green-800 text-white rounded-none text-xs" data-testid={`publish-reply-${r.id}`}>Опубликовать с ответом</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setReplyId(null); setReplyText(""); }} className="text-xs">Отмена</Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {r.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => moderate(r.id, "published")} className="bg-green-800 hover:bg-green-700 text-white rounded-none text-xs gap-1" data-testid={`approve-${r.id}`}>
                      <Check className="w-3 h-3" /> Одобрить
                    </Button>
                    <Button size="sm" onClick={() => moderate(r.id, "rejected")} className="bg-red-900 hover:bg-red-800 text-white rounded-none text-xs gap-1" data-testid={`reject-${r.id}`}>
                      <X className="w-3 h-3" /> Отклонить
                    </Button>
                  </>
                )}
                {replyId !== r.id && (
                  <Button size="sm" variant="ghost" onClick={() => { setReplyId(r.id); setReplyText(r.admin_reply || ""); }} className="text-muted-foreground text-xs gap-1" data-testid={`reply-btn-${r.id}`}>
                    <MessageSquare className="w-3 h-3" /> Ответить
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
